import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import OverviewPage from "@/pages/OverviewPage";
import TerritoryPage from "@/pages/TerritoryPage";
import DistributionPage from "@/pages/DistributionPage";
import SchemesPage from "@/pages/SchemesPage";
import AIInsightsPage from "@/pages/AIInsightsPage";
import ConfigurationPage from "@/pages/ConfigurationPage";
import PerformancePage from "@/pages/PerformancePage";
import AlertsPage from "@/pages/AlertsPage";
import MapPage from "@/pages/MapPage";
import ComparisonPage from "@/pages/ComparisonPage";
import AuditLogPage from "@/pages/AuditLogPage";
import UserManagementPage from "@/pages/UserManagementPage";
import ProductsPage from "@/pages/ProductsPage";
import VisitsPage from "@/pages/VisitsPage";
import OutletLookupPage from "@/pages/OutletLookupPage";
import BeatPlanPage from "@/pages/BeatPlanPage";
import GuidePage from "@/pages/GuidePage";
import LeaderLinkPage from "@/pages/LeaderLinkPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <img src="/keya-logo.png" alt="Keya" className="w-16 h-16 object-contain animate-pulse" />
        <p className="text-muted-foreground font-body text-sm">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleRoute({ module, children }) {
  const { hasModule } = useAuth();
  if (!hasModule(module)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      {/* A leader's standing link: no sign-in, the token is the key */}
      <Route path="/l/:token" element={<LeaderLinkPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<OverviewPage />} />
        <Route path="territory" element={<TerritoryPage />} />
        <Route path="map" element={<MapPage />} />
        <Route path="comparison" element={<RoleRoute module="comparison"><ComparisonPage /></RoleRoute>} />
        <Route path="distribution" element={<DistributionPage />} />
        <Route path="schemes" element={<SchemesPage />} />
        <Route path="products" element={<RoleRoute module="products"><ProductsPage /></RoleRoute>} />
        <Route path="visits" element={<RoleRoute module="visits"><VisitsPage /></RoleRoute>} />
        <Route path="outlets" element={<RoleRoute module="outlets"><OutletLookupPage /></RoleRoute>} />
        <Route path="beat-plans" element={<RoleRoute module="beat-plans"><BeatPlanPage /></RoleRoute>} />
        <Route path="performance" element={<RoleRoute module="performance"><PerformancePage /></RoleRoute>} />
        <Route path="alerts" element={<RoleRoute module="alerts"><AlertsPage /></RoleRoute>} />
        <Route path="ai-insights" element={<RoleRoute module="ai-insights"><AIInsightsPage /></RoleRoute>} />
        <Route path="users" element={<RoleRoute module="users"><UserManagementPage /></RoleRoute>} />
        <Route path="audit-log" element={<RoleRoute module="audit-log"><AuditLogPage /></RoleRoute>} />
        <Route path="configuration" element={<RoleRoute module="configuration"><ConfigurationPage /></RoleRoute>} />
        <Route path="guide" element={<GuidePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
